"use client";

import Link from "next/link";
import { ArrowLeft, Mail, MessageCircle, FileText, Shield } from "lucide-react";

export default function SupportPage() {
  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-3xl mx-auto px-6 py-12">
        {/* Header */}
        <header className="mb-12">
          <Link
            href="/"
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to app
          </Link>
          <h1 className="text-3xl font-bold text-foreground mb-2">
            Donezo Support
          </h1>
          <p className="text-muted-foreground">
            If you need help, please contact us here.
          </p>
        </header>

        {/* FAQ Section */}
        <section className="mb-12">
          <h2 className="text-xl font-semibold text-foreground mb-6 flex items-center gap-2">
            <MessageCircle className="w-5 h-5" />
            Frequently Asked Questions
          </h2>
          <div className="space-y-6">
            <FaqItem
              question="Where is my data stored?"
              answer="Your data is securely stored on cloud servers. A local backup is also kept so you can use the app offline."
            />
            <FaqItem
              question="Can I use it offline?"
              answer="Yes, you can work offline. When you're back online, your data will automatically sync."
            />
            <FaqItem
              question="Can I use it on multiple devices?"
              answer="Yes, sign in with the same account to sync your data across multiple devices."
            />
            <FaqItem
              question="I deleted a task. Can I restore it?"
              answer="Completed tasks are saved in history and can be restored from the completed tasks list. Please note that deleted tasks cannot be recovered."
            />
            <FaqItem
              question="Are there keyboard shortcuts?"
              answer="Yes, we support many keyboard shortcuts. Press '?' in the app to see the full list."
            />
            <FaqItem
              question="How do I delete my account?"
              answer="If you wish to delete your account, please contact us using the information below."
            />
          </div>
        </section>

        {/* Contact Section */}
        <section className="mb-12">
          <h2 className="text-xl font-semibold text-foreground mb-6 flex items-center gap-2">
            <Mail className="w-5 h-5" />
            Contact Us
          </h2>
          <div className="bg-muted/50 rounded-lg p-6">
            <p className="text-foreground mb-4">
              If you have any questions or requests, please feel free to contact us at the email address below.
            </p>
            <a
              href="mailto:n.shinzo1010@icloud.com"
              className="inline-flex items-center gap-2 text-accent-300 hover:underline font-medium"
            >
              <Mail className="w-4 h-4" />
              n.shinzo1010@icloud.com
            </a>
            <p className="text-sm text-muted-foreground mt-4">
              We typically respond within 2 business days.
            </p>
            <div className="mt-6 pt-6 border-t border-border">
              <p className="text-foreground mb-3">
                機能改良のご要望・バグのご報告は、フォームからも送信できます。
              </p>
              <Link
                href="/contact"
                className="inline-flex items-center gap-2 text-accent-300 hover:underline font-medium"
              >
                <MessageCircle className="w-4 h-4" />
                お問い合わせフォームを開く
              </Link>
            </div>
          </div>
        </section>

        {/* App Info */}
        <section className="mb-12">
          <h2 className="text-xl font-semibold text-foreground mb-6 flex items-center gap-2">
            <FileText className="w-5 h-5" />
            App Information
          </h2>
          <div className="bg-muted/50 rounded-lg p-6 space-y-3">
            <div className="flex justify-between">
              <span className="text-muted-foreground">App Name</span>
              <span className="text-foreground font-medium">Donezo</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Version</span>
              <span className="text-foreground font-medium">1.0.0</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Platform</span>
              <span className="text-foreground font-medium">macOS</span>
            </div>
          </div>
        </section>

        {/* Legal Links */}
        <section className="mb-12">
          <h2 className="text-xl font-semibold text-foreground mb-6 flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Legal Information
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Link
              href="/privacy"
              className="bg-muted/50 rounded-lg p-4 hover:bg-muted transition-colors text-center"
            >
              <span className="text-foreground font-medium">
                Privacy Policy
              </span>
            </Link>
            <Link
              href="/terms"
              className="bg-muted/50 rounded-lg p-4 hover:bg-muted transition-colors text-center"
            >
              <span className="text-foreground font-medium">Terms of Service</span>
            </Link>
            <Link
              href="/tokushoho"
              className="bg-muted/50 rounded-lg p-4 hover:bg-muted transition-colors text-center"
            >
              <span className="text-foreground font-medium">特定商取引法に基づく表記</span>
            </Link>
          </div>
        </section>

        {/* Footer */}
        <footer className="pt-8 border-t border-border text-center text-sm text-muted-foreground">
          <p>&copy; 2025 Donezo. All rights reserved.</p>
        </footer>
      </div>
    </div>
  );
}

function FaqItem({ question, answer }: { question: string; answer: string }) {
  return (
    <div className="border-b border-border pb-4">
      <h3 className="font-medium text-foreground mb-2">{question}</h3>
      <p className="text-muted-foreground text-sm">{answer}</p>
    </div>
  );
}
